import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	useMutation,
	UseMutationOptions,
	useQueryClient,
	useQuery,
	UseQueryOptions,
} from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import { FormikHelpers } from 'formik'
import axiosLibrary, { AxiosResponse } from 'axios'
import { useUrlQuery } from 'hooks/url'
import { errorNotify, successNotify } from 'utils/toster'
import { ENDPOINTS } from 'const/endpoints'
import { scrollToError } from 'utils/common'
import { commonMessages } from 'utils/messages'
import { IFullSession, ISessionParams } from '../Session.interfaces'
import { SessionInitialState, SessionPayload } from './Session.types'
import {
	cancelFileUpload,
	createSession,
	fileUpload,
	getProgramViewSession,
	getSuggestionSession,
	updateProgramSession,
} from '../Session.service'
import { getClientDetails } from 'hooks/common'
import { IAxiosResponseWithPagination } from 'types/entities'
import axios from 'axios'
import { convertUnix, startOfDay } from 'utils/time'

//* Formik useInitial hook
export function useInitial() {
	/**
	 * * Variables and hooks section
	 */

	const alreadyMounted = useRef(false)
	const [locationQuery] = useUrlQuery<any>()
	const id = locationQuery?.suggestionSession || locationQuery?.session
	const { data, isFetched, isFetching } = useProgramViewSession({
		id,
		type: locationQuery?.suggestionType,
		clientId: getClientDetails()?.clientId,
		suspense: !id, //TO USE SUSPENSE ONCE ON MOUNT
	})
	/**
	 * *useEffect Section
	 * @returns useEffect
	 */
	useEffect(() => {
		alreadyMounted.current = true
	}, [isFetched, isFetching])
	/**
	 * *useMemo Section
	 */
	return useMemo(() => {
		const session = new SessionInitialState(
			id ? { ...data?.data, isFetched: !isFetched || isFetching } : { isFetched: false },
		)
		return session
	}, [data?.data, isFetching, id, isFetching, open])
}
//* Formik useSubmitHandler hook
export function useSubmitHandler(isMultiEnrolled: boolean, currentDate: number) {
	/**
	 * * Variables and hooks section
	 */
	const [urlQuery, setUrlQuery] = useUrlQuery<any>()
	const sessionId = urlQuery?.session
	const params = useParams<{ id: string }>()
	const id = params.id
	const client = useQueryClient()
	const { mutateAsync: create } = useCreateSession({ clientId: getClientDetails()?.clientId })
	const { mutateAsync: update } = useUpdateSession({
		id: sessionId,
		clientId: getClientDetails()?.clientId,
	})
	/**
	 * * useCallback section
	 */
	return useCallback(
		async (form: SessionInitialState, formikHelpers: FormikHelpers<SessionInitialState>) => {
			formikHelpers.setSubmitting(true)
			form.program = id
			form.isMultiEnrolled = isMultiEnrolled
			form.currentDate = currentDate
			form.existingId = urlQuery?.suggestionSession

			try {
				if (!sessionId) {
					// if(form?.search){
					// 	createSuggestionSession
					// }
					await create(new SessionPayload(form))
				} else {
					await update(new SessionPayload(form))
				}
				formikHelpers.setSubmitting(false)
				client.invalidateQueries([ENDPOINTS.SESSIONS])
				setUrlQuery(() => {
					return {
						suggestionSession: '',
						suggestionType: '',
						session: '',
						addSession: '',
					}
				})
				successNotify(sessionId ? commonMessages.updateMessage : commonMessages.createMessage)
			} catch (err: any) {
				if (err.response.data && err.response.data.message) {
					errorNotify(err.response.data.message)
					// Somewhere else, even another file
				} else {
					formikHelpers.setErrors(err.response.data) //TODO - ADD ERRORS TRANSFORMATION TO FORM FORMAT
					errorNotify(commonMessages.errorHandlingMessage)
				}
				scrollToError()
			}
		},
		[client, id, create, update, urlQuery, setUrlQuery],
	)
}

/**
 * * Using the React Hooks useMutation to create a mutation function for creating a session.
 */
type ICreateSessionProps = Partial<
	UseMutationOptions<AxiosResponse<SessionPayload>, any, Partial<SessionPayload>>
>

export function useCreateSession({ clientId, ...rest }: ICreateSessionProps | any = {}) {
	return useMutation<AxiosResponse<SessionPayload>, any, Partial<SessionPayload>>(
		async (data) => await createSession(clientId, data),

		{
			...rest,
		},
	)
}
/**
 * * Using the React Hooks useMutation to update a mutation function for update a session.
 */
interface IUpdateSessionProps
	extends Partial<UseMutationOptions<AxiosResponse<SessionPayload>, any, Partial<SessionPayload>>> {
	id?: string
	clientId?: string
}

export function useUpdateSession({ id, clientId, ...rest }: IUpdateSessionProps) {
	return useMutation<AxiosResponse<SessionPayload>, any, Partial<SessionPayload>>(
		async (data) => await updateProgramSession(id, clientId, data),
		{
			...rest,
		},
	)
}

/**
 * Using the React Hooks useMutation for file upload
 */

export function useFileUpload({
	fileId,
	startChunk,
	endChunk,
	fileSize,
	clientId,
	totalChunks,
	totalChunksUploaded,
	contentType = '',
	...rest
}: any) {
	return useMutation<AxiosResponse<any>, any, any>(
		async (data) => {
			return await fileUpload(
				fileId,
				startChunk,
				endChunk,
				fileSize,
				clientId,
				totalChunks,
				totalChunksUploaded,
				contentType,
				data,
			)
		},
		{
			...rest,
		},
	)
}

export function useCancelFileUpload({ session, clientId }: { session?: string; clientId: string }) {
	//@ts-ignore
	const CancelToken = axiosLibrary.CancelToken
	const source = CancelToken.source()
	return useMutation<AxiosResponse<any>, any, any>(
		async (id: string) => await cancelFileUpload(clientId, { path: id, session: session }, source),
	)
}

/**
 * * Using the React Hooks useQuery to update a Query function for view a session.
 */
interface IProgramViewSession
	extends Partial<UseQueryOptions<AxiosResponse<IFullSession>>>,
		Partial<IFullSession> {
	id?: string
	clientId?: string
}
export function useProgramViewSession({ id, clientId, ...rest }: IProgramViewSession) {
	return useQuery<AxiosResponse<IFullSession>, any, AxiosResponse<IFullSession>>(
		[ENDPOINTS.SESSION_ENTITY, id, clientId],
		() => {
			const CancelToken = axiosLibrary.CancelToken
			const source = CancelToken.source()
			return getProgramViewSession(id, clientId, source)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			suspense: true,
			enabled: !!id,
			...rest,
		},
	)
}

interface ISuggestionSessionQueryProps
	extends UseQueryOptions<IAxiosResponseWithPagination<Partial<IFullSession>>>,
		Partial<ISessionParams> {
	isEnabled?: boolean
}

export function useProgramSuggestionSession({
	type,
	typeId,
	title,
	duration,
	toBeCompleted,
	frequency,
	questionnaireId,
	page,
	sort,
	limit,
	search,
	programId,
	isEnabled = true,
	currentDate = convertUnix(startOfDay()),
	isGlobal,
	id,
	...rest
}: ISuggestionSessionQueryProps = {}) {
	return useQuery<
		IAxiosResponseWithPagination<Partial<IFullSession>>,
		any,
		IAxiosResponseWithPagination<Partial<IFullSession>>
	>(
		[
			ENDPOINTS.SESSIONS_SUGGESTION,
			page,
			sort,
			limit,
			search,
			id,
			typeId,
			programId,
			type,
			isGlobal,
		],
		() => {
			const CancelToken = axios.CancelToken
			const source = CancelToken.source()
			return getSuggestionSession(
				{
					type,
					typeId,
					title,
					duration,
					toBeCompleted,
					frequency,
					questionnaireId,
					page,
					sort,
					limit,
					search,
					programId,
					currentDate,
					isGlobal,
				},
				source,
				// id,
			)
		},
		{
			keepPreviousData: true,
			refetchOnWindowFocus: false,
			suspense: false,
			enabled: !!typeId && isEnabled,
			...rest,
		},
	)
}
